/**
 * Profile Recommendation Engine
 *
 * Multi-signal scoring system that recommends a threat profile based on:
 * - Industry selection (bs-0): Primary signal, +40 points max
 * - Data types (dt-1): +20 points max for regulated data
 * - Regulatory frameworks (rf-1): +30 points max for compliance requirements
 * - Market segment (revenue + headcount): +10 points for size tier matching
 *
 * Active profiles: TP-HC, TP-FIN-BK, TP-FIN-INS, TP-GOV, TP-EDU, TP-NP, TP-RE, TP-DEFAULT
 */

import { getProfileSignalKey } from '../data/industries'

// Profile definitions with industry category mappings
const PROFILE_MAPPINGS = {
  'TP-HC': {
    name: 'Healthcare',
    industryCategories: ['healthcare'],
    dataTypes: ['phi'],
    frameworks: ['hipaa', 'hitech'],
    description: 'Healthcare providers, clinics, and medical practices handling protected health information'
  },
  'TP-FIN-BK': {
    name: 'Financial Services: Banking & Credit Unions',
    industryCategories: ['financial-banking', 'financial-services'],
    dataTypes: ['pci', 'financial-data'],
    frameworks: ['glba', 'pci-dss', 'ffiec', 'ftc-safeguards', 'bsa-aml'],
    description: 'Community banks, credit unions, and regional financial institutions'
  },
  'TP-FIN-INS': {
    name: 'Financial Services: Insurance',
    industryCategories: ['financial-insurance'],
    dataTypes: ['pci', 'financial-data'],
    frameworks: ['glba', 'naic-mdl668'],
    description: 'Property/casualty, life, health insurers, and insurance agencies'
  },
  'TP-GOV': {
    name: 'Government & Public Sector',
    industryCategories: ['government'],
    dataTypes: ['cui', 'pii'],
    frameworks: ['nist-800-171', 'cmmc', 'state-privacy'],
    description: 'Local, county, state, and tribal government agencies and quasi-governmental entities'
  },
  'TP-EDU': {
    name: 'Education',
    industryCategories: ['education'],
    dataTypes: ['pii', 'ferpa-data'],
    frameworks: ['ferpa'],
    description: 'K-12 school districts, higher education institutions, and private schools'
  },
  'TP-NP': {
    name: 'Nonprofit Organization',
    industryCategories: ['nonprofit'],
    dataTypes: ['donor-data', 'pii'],
    frameworks: ['state-privacy'],
    description: 'Nonprofit organizations including human services, foundations, and membership associations'
  },
  'TP-RE': {
    name: 'Real Estate',
    industryCategories: ['real-estate'],
    dataTypes: ['tenant-data', 'investor-data', 'pii'],
    frameworks: ['state-privacy', 'sec-cyber', 'fincen-gto', 'ofac', 'pci-dss', 'fcra'],
    description: 'Real estate operators, REITs, property management firms, and commercial/residential ownership entities'
  },
  'TP-DEFAULT': {
    name: 'General: Regulated Organization',
    industryCategories: ['professional-services', 'technology', 'manufacturing', 'retail', 'energy', 'transportation', 'legal', 'general'],
    dataTypes: ['pii', 'client-confidential'],
    frameworks: ['state-privacy', 'soc2', 'ccpa', 'irs-4557'],
    description: 'Organizations across various industries with standard regulatory requirements'
  }
}

// Signal weights
const WEIGHTS = {
  industry: 40,      // Primary signal
  dataTypes: 20,     // Data type matches
  frameworks: 30,    // Regulatory framework matches
  marketSegment: 10  // Size/segment alignment
}

// Market segment derivation from revenue and headcount
const MARKET_SEGMENTS = {
  'micro': { revenueRange: ['Under $25M', '<$5mm', '<$25mm'], headcountRange: ['1–15'] },
  'small': { revenueRange: ['Under $25M', '$25M \u2013 $50M', '$5mm - $25mm', '<$25mm'], headcountRange: ['16–50'] },
  'medium': { revenueRange: ['$25M \u2013 $50M', '$50M \u2013 $100M', '$100M \u2013 $250M', '$25mm - $100mm'], headcountRange: ['51–250'] },
  'large': { revenueRange: ['$250M \u2013 $500M', 'Over $500M', '>$100mm', '>$500mm'], headcountRange: ['250+'] }
}

/**
 * Derive market segment from revenue and headcount
 * @param {string} revenue - Annual revenue range
 * @param {string} headcount - Staffing size range
 * @returns {string} Market segment: micro, small, medium, or large
 */
export function deriveMarketSegment(revenue, headcount) {
  // Score-based approach: higher = larger organization
  const revenueScores = {
    'Under $25M': 2,
    '$25M \u2013 $50M': 3,
    '$50M \u2013 $100M': 3,
    '$100M \u2013 $250M': 3,
    '$250M \u2013 $500M': 4,
    'Over $500M': 4,
    // Legacy mappings
    '<$5mm': 1,
    '<$25mm': 2,
    '$5mm - $25mm': 2,
    '$25mm - $100mm': 3,
    '>$100mm': 4
  }

  const headcountScores = {
    '1–15': 1,
    '16–50': 2,
    '51–250': 3,
    '250+': 4
  }

  const revScore = revenueScores[revenue] || 2
  const hcScore = headcountScores[headcount] || 2
  const avgScore = (revScore + hcScore) / 2

  if (avgScore <= 1.5) return 'micro'
  if (avgScore <= 2.5) return 'small'
  if (avgScore <= 3.5) return 'medium'
  return 'large'
}

/**
 * Calculate profile scores based on all input signals
 * @param {Object} inputs - Input signals for scoring
 * @param {string} inputs.industryCategory - Industry category from bs-0
 * @param {string[]} inputs.dataTypes - Selected data types from dt-1
 * @param {string[]} inputs.frameworks - Selected frameworks from rf-1
 * @param {string[]} inputs.orgCharacteristics - Selected org characteristics from oc-1
 * @param {string} inputs.marketSegment - Derived market segment
 * @returns {Object} Profile scores with breakdown
 */
export function calculateProfileScores(inputs) {
  const {
    industryCategory = 'general',
    dataTypes = [],
    frameworks = [],
    orgCharacteristics = [],
    marketSegment = 'small'
  } = inputs

  const scores = {}

  // Calculate scores for each active profile
  for (const [profileId, profile] of Object.entries(PROFILE_MAPPINGS)) {
    let score = 0
    const breakdown = {
      industry: 0,
      dataTypes: 0,
      frameworks: 0,
      marketSegment: 0
    }

    // Industry match (40 points max)
    if (profile.industryCategories.includes(industryCategory)) {
      breakdown.industry = WEIGHTS.industry
      score += WEIGHTS.industry
    }

    // Data types match (20 points max)
    const dataTypeMatches = dataTypes.filter(dt => profile.dataTypes.includes(dt))
    if (dataTypeMatches.length > 0) {
      // Scale points based on match percentage, with diminishing returns
      const matchRatio = Math.min(dataTypeMatches.length / profile.dataTypes.length, 1)
      breakdown.dataTypes = Math.round(WEIGHTS.dataTypes * Math.sqrt(matchRatio))
      score += breakdown.dataTypes
    }

    // Framework match (30 points max)
    const frameworkMatches = frameworks.filter(fw => profile.frameworks.includes(fw))
    if (frameworkMatches.length > 0) {
      // Direct framework matches are strong signals
      const matchRatio = Math.min(frameworkMatches.length / profile.frameworks.length, 1)
      breakdown.frameworks = Math.round(WEIGHTS.frameworks * matchRatio)
      score += breakdown.frameworks
    }

    // Market segment alignment (10 points max)
    if (profileId === 'TP-NP' || profileId === 'TP-DEFAULT' || profileId === 'TP-EDU') {
      // Common at all sizes
      breakdown.marketSegment = WEIGHTS.marketSegment
    } else if (['medium', 'large'].includes(marketSegment)) {
      // Healthcare, Financial, Government more common at larger sizes
      breakdown.marketSegment = WEIGHTS.marketSegment
    } else {
      breakdown.marketSegment = Math.round(WEIGHTS.marketSegment * 0.7)
    }
    score += breakdown.marketSegment

    // Org characteristic modifiers
    if (orgCharacteristics.includes('nonprofit') && profileId === 'TP-NP') {
      score += 15 // Strong boost for explicit nonprofit selection
      breakdown.orgCharacteristics = 15
    }
    if (orgCharacteristics.includes('government-contractor') && profileId === 'TP-GOV') {
      score += 10
      breakdown.orgCharacteristics = (breakdown.orgCharacteristics || 0) + 10
    }

    scores[profileId] = {
      score,
      breakdown,
      maxPossible: WEIGHTS.industry + WEIGHTS.dataTypes + WEIGHTS.frameworks + WEIGHTS.marketSegment
    }
  }

  return scores
}

/**
 * Get recommended profile based on scores
 * @param {Object} scores - Profile scores from calculateProfileScores
 * @returns {Object} Recommended profile with confidence level
 */
export function getRecommendedProfile(scores) {
  // Find profile with highest score
  let topProfile = null
  let topScore = 0
  let secondScore = 0

  for (const [profileId, data] of Object.entries(scores)) {
    if (data.score > topScore) {
      secondScore = topScore
      topScore = data.score
      topProfile = profileId
    } else if (data.score > secondScore) {
      secondScore = data.score
    }
  }

  // Calculate confidence based on score differential and absolute score
  const scoreDiff = topScore - secondScore
  const maxPossible = scores[topProfile]?.maxPossible || 100
  const scoreRatio = topScore / maxPossible

  let confidence = 'low'
  if (scoreRatio >= 0.7 && scoreDiff >= 20) {
    confidence = 'high'
  } else if (scoreRatio >= 0.5 || scoreDiff >= 15) {
    confidence = 'medium'
  }

  // Generate explanation
  const breakdown = scores[topProfile]?.breakdown || {}
  const signals = []

  if (breakdown.industry >= 30) {
    signals.push('industry selection')
  }
  if (breakdown.frameworks >= 20) {
    signals.push('regulatory requirements')
  }
  if (breakdown.dataTypes >= 15) {
    signals.push('data types handled')
  }
  if (breakdown.orgCharacteristics >= 10) {
    signals.push('organizational characteristics')
  }

  const explanation = signals.length > 0
    ? `Based on ${signals.join(', ')}`
    : 'Based on general organizational profile'

  return {
    profileId: topProfile,
    profileName: PROFILE_MAPPINGS[topProfile]?.name || 'General: Regulated Organization',
    description: PROFILE_MAPPINGS[topProfile]?.description || '',
    score: topScore,
    maxPossible,
    confidence,
    explanation,
    breakdown: scores[topProfile]?.breakdown,
    allScores: scores
  }
}

/**
 * Main function: Get profile recommendation from all inputs
 * @param {Object} params - All input parameters
 * @param {Object} params.client - Client data (industry, annualRevenue, staffingSize)
 * @param {Object} params.businessScoping - Business scoping responses
 * @returns {Object} Profile recommendation with confidence and explanation
 */
export function recommendProfile({ client = {}, businessScoping = {} }) {
  // Extract industry category from bs-0 response
  const bs0Response = businessScoping['bs-0']
  let industryCategory = 'general'

  if (bs0Response?.industryCategory) {
    industryCategory = bs0Response.industryCategory
  } else if (bs0Response?.value) {
    // Legacy format: try to map from label
    industryCategory = mapIndustryLabelToCategory(bs0Response.value)
  } else if (client.industry) {
    // Fallback to client industry - use composite key from industry + sub-category
    const signalKey = getProfileSignalKey(client.industry, client.industrySubCategory)
    industryCategory = mapIndustryLabelToCategory(signalKey || client.industry)
  }

  // Extract data types from dt-1 response
  const dt1Response = businessScoping['dt-1']
  const dataTypes = Array.isArray(dt1Response)
    ? dt1Response
    : (dt1Response?.value || [])

  // Extract frameworks from rf-1 response
  const rf1Response = businessScoping['rf-1']
  const frameworks = Array.isArray(rf1Response)
    ? rf1Response
    : (rf1Response?.value || [])

  // Extract org characteristics from oc-1 response
  const oc1Response = businessScoping['oc-1']
  const orgCharacteristics = Array.isArray(oc1Response)
    ? oc1Response
    : (oc1Response?.value || [])

  // Derive market segment
  const revenue = businessScoping['bs-1']?.value || client.annualRevenue || 'Under $25M'
  const headcount = businessScoping['bs-3']?.value || client.staffingSize || '16–50'
  const marketSegment = deriveMarketSegment(revenue, headcount)

  // Calculate scores
  const scores = calculateProfileScores({
    industryCategory,
    dataTypes,
    frameworks,
    orgCharacteristics,
    marketSegment
  })

  // Get recommendation
  const recommendation = getRecommendedProfile(scores)

  // Resolve sub-industry key from client data
  let subIndustryKey = null
  if (client?.industry_sub_category) {
    subIndustryKey = client.industry_sub_category
  } else if (client?.industrySubCategory) {
    subIndustryKey = client.industrySubCategory
  }

  return {
    ...recommendation,
    subIndustryKey
  }
}

/**
 * Map industry label or signal key to category for profile matching
 * @param {string} label - Industry label from selection or signal key from getProfileSignalKey
 * @returns {string} Industry category matching PROFILE_MAPPINGS.industryCategories
 */
function mapIndustryLabelToCategory(label) {
  if (!label) return 'general'

  const lowerLabel = label.toLowerCase()

  // Handle signal key format from getProfileSignalKey (e.g., 'financial-services-banking')
  // These are the most specific matches — check first
  if (lowerLabel === 'financial-services-banking') return 'financial-banking'
  if (lowerLabel === 'financial-services-insurance') return 'financial-insurance'

  // Generic financial services sub-industries (fintech, investment, wealth, other)
  // These do NOT map to banking — they use TP-DEFAULT with appropriate frameworks
  if (lowerLabel === 'financial-services') return 'financial-banking'

  // Handle normalized signal key formats from getProfileSignalKey
  if (lowerLabel === 'healthcare') return 'healthcare'
  if (lowerLabel === 'non-profit') return 'nonprofit'
  if (lowerLabel === 'government') return 'government'
  if (lowerLabel === 'education') return 'education'
  if (lowerLabel === 'professional-services') return 'professional-services'
  if (lowerLabel === 'technology') return 'technology'
  if (lowerLabel === 'manufacturing') return 'manufacturing'
  if (lowerLabel === 'energy') return 'energy'
  if (lowerLabel === 'retail') return 'retail'
  if (lowerLabel === 'real-estate') return 'real-estate'
  if (lowerLabel === 'transportation') return 'transportation'
  if (lowerLabel === 'legal') return 'legal'

  // Handle label formats (e.g., 'Healthcare', 'Financial Services - Banking')
  if (lowerLabel.includes('healthcare') || lowerLabel.includes('health plan')) return 'healthcare'
  if (lowerLabel.includes('banking') || lowerLabel.includes('credit union') || lowerLabel.includes('credit-union')) return 'financial-banking'
  if (lowerLabel.includes('insurance')) return 'financial-insurance'
  // Only match specific financial sub-types to banking/insurance, not generic "financial"
  if (lowerLabel.includes('financial services - banking') || lowerLabel.includes('financial services - credit')) return 'financial-banking'
  if (lowerLabel.includes('financial services - insurance')) return 'financial-insurance'
  if (lowerLabel.includes('financial')) return 'financial-banking'
  if (lowerLabel.includes('nonprofit') || lowerLabel.includes('non-profit') || lowerLabel.includes('foundation') || lowerLabel.includes('membership')) return 'nonprofit'
  if (lowerLabel.includes('government') || lowerLabel.includes('tribal') || lowerLabel.includes('municipal')) return 'government'
  if (lowerLabel.includes('education') || lowerLabel.includes('school') || lowerLabel.includes('university') || lowerLabel.includes('k-12') || lowerLabel.includes('k12')) return 'education'
  if (lowerLabel.includes('legal') || lowerLabel.includes('accounting') || lowerLabel.includes('consulting') || lowerLabel.includes('professional')) return 'professional-services'
  if (lowerLabel.includes('technology') || lowerLabel.includes('saas') || lowerLabel.includes('software') || lowerLabel.includes('msp')) return 'technology'
  if (lowerLabel.includes('manufactur')) return 'manufacturing'
  if (lowerLabel.includes('energy') || lowerLabel.includes('utilit')) return 'energy'
  if (lowerLabel.includes('retail') || lowerLabel.includes('hospitality')) return 'retail'
  if (lowerLabel.includes('real estate') || lowerLabel.includes('property')) return 'real-estate'
  if (lowerLabel.includes('transport') || lowerLabel.includes('logistics')) return 'transportation'

  return 'general'
}

/**
 * Get all available active profiles
 * @returns {Object[]} Array of profile objects
 */
export function getActiveProfiles() {
  return Object.entries(PROFILE_MAPPINGS).map(([id, profile]) => ({
    id,
    ...profile
  }))
}

/**
 * Check if a profile ID is active
 * @param {string} profileId - Profile ID to check
 * @returns {boolean} True if profile is active
 */
export function isActiveProfile(profileId) {
  return Object.keys(PROFILE_MAPPINGS).includes(profileId)
}

export default {
  recommendProfile,
  calculateProfileScores,
  getRecommendedProfile,
  deriveMarketSegment,
  getActiveProfiles,
  isActiveProfile
}
